import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useTenant } from '../../context/TenantContext';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../utils/format';

import DashboardHeader from './components/DashboardHeader';
import EmployeeRequestsPanel from './components/EmployeeRequestsPanel';
import MetricsCard from './components/MetricsCard';
import TrendChart from './components/TrendChart';
import QuartileChart from './components/QuartileChart';
import CategoriesOfWorkersTable from './components/CategoriesOfWorkersTable';
import VariableCompensationPanel from './components/VariableCompensationPanel';
import AssessmentAlert from './components/AssessmentAlert';
import AIInsightsPanel from './components/AIInsightsPanel';
import ReportGenerationToast from './components/ReportGenerationToast';

// PDF generation is ~1.5MB of bundle weight. Lazy-load so it only ships
// when a user actually clicks the report button.
const loadPdfModule = () => import('../../pdf/DirectiveReportPDF');

const AnalyticsDashboard = () => {
  const { tenant, isTransitioning } = useTenant();
  const { t, i18n } = useTranslation();
  const { dateTime } = useFormatters();
  const [isLoading, setIsLoading] = useState(false);
  const [reportState, setReportState] = useState({ phase: 'idle' });

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 900);
  };

  const handleExport = async () => {
    // "Export" is a quick snapshot, same PDF as Directive report for now
    handleGenerateReport();
  };

  const handleGenerateReport = async () => {
    setReportState({ phase: 'generating' });
    try {
      // Defer slightly so toast renders before heavy work blocks
      await new Promise((r) => setTimeout(r, 50));
      // Lazy-load the PDF module on first use
      const { downloadDirectiveReport } = await loadPdfModule();
      const result = await downloadDirectiveReport(tenant);
      setReportState({ phase: 'success', filename: result.filename, sizeBytes: result.sizeBytes });
      setTimeout(() => setReportState({ phase: 'idle' }), 5000);
    } catch (e) {
      setReportState({ phase: 'error', error: e.message });
      setTimeout(() => setReportState({ phase: 'idle' }), 6000);
    }
  };

  const m = tenant.metrics;

  // Build directive-aligned metrics
  const metrics = [
    {
      title: 'Mean pay gap',
      value: m.meanPayGap,
      change: m.meanPayGapDelta,
      changeType: m.meanPayGapDelta < 0 ? 'positive' : m.meanPayGapDelta > 0 ? 'negative' : 'neutral',
      icon: 'TrendingUp',
      alert: m.meanPayGap > 5 ? 'critical' : m.meanPayGap > 4 ? 'warning' : null,
      description: m.meanPayGap > 5
        ? 'Exceeds 5% threshold, Art. 10 assessment required'
        : m.meanPayGap > 4
        ? 'Approaching threshold, monitor closely'
        : 'Within compliant range',
      directiveRef: 'Art. 9(a)',
    },
    {
      title: 'Median pay gap',
      value: m.medianPayGap,
      change: m.medianPayGapDelta,
      changeType: m.medianPayGapDelta < 0 ? 'positive' : m.medianPayGapDelta > 0 ? 'negative' : 'neutral',
      icon: 'BarChart3',
      alert: m.medianPayGap > 5 ? 'critical' : null,
      description: 'Per categories of workers',
      directiveRef: 'Art. 9(c)',
    },
    {
      title: 'Active assessments',
      value: m.activeAssessments,
      unit: '',
      change: null,
      icon: 'FileCheck',
      alert: m.activeAssessments > 0 ? (m.activeAssessments >= 2 ? 'critical' : 'warning') : null,
      description: m.activeAssessments > 0
        ? `${m.activeAssessments} joint pay assessment${m.activeAssessments > 1 ? 's' : ''} in progress`
        : 'No triggered assessments',
      directiveRef: 'Art. 10',
    },
    {
      title: 'Compliance score',
      value: m.complianceScore,
      change: null,
      icon: 'Shield',
      alert: m.complianceScore < 70 ? 'critical' : m.complianceScore < 85 ? 'warning' : null,
      description: m.complianceScore >= 85
        ? 'Strong directive alignment'
        : m.complianceScore >= 70
        ? 'Gaps in directive alignment'
        : 'Directive obligations unmet',
    },
  ];

  return (
    <>
      <Helmet>
        <title>{tenant.name} Analytics Dashboard · ClearEqual</title>
        <meta
          name="description"
          content="Real-time gender pay gap analytics and EU Pay Transparency Directive compliance monitoring"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <Breadcrumb />

            <DashboardHeader
              tenant={tenant}
              onRefresh={handleRefresh}
              onExport={handleExport}
              onGenerateReport={handleGenerateReport}
              isLoading={isLoading}
            />

            {/* Employee Information Requests, Article 7 worker rights workflow.
                Appears between header and metrics so HR admin sees pending
                employee requests immediately on landing. */}
            <EmployeeRequestsPanel />

            {/* Content fades during tenant transitions for a polished feel */}
            <div
              className={`transition-opacity duration-300 ${
                isTransitioning ? 'opacity-40' : 'opacity-100'
              }`}
            >
              {/* Metrics grid, Art. 9(a)(c) + active assessments + compliance */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {metrics.map((metric) => (
                  <MetricsCard
                    key={metric.title}
                    title={metric.title}
                    value={metric.value}
                    unit={metric.unit}
                    change={metric.change}
                    changeType={metric.changeType}
                    icon={metric.icon}
                    alert={metric.alert}
                    description={metric.description}
                    directiveRef={metric.directiveRef}
                  />
                ))}
              </div>

              {/* AI Insights, full width hero panel, the differentiator.
                  Panel fetches insights itself via aiInsightsService, see
                  src/services/aiInsightsService.js for the production swap. */}
              <div className="mb-6">
                <AIInsightsPanel tenant={tenant} />
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2">
                  <TrendChart data={tenant.trendData} title={t('dashboard.trend.titleAlt')} />
                </div>
                <div className="lg:col-span-1">
                  <VariableCompensationPanel metrics={m} />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2">
                  <QuartileChart data={tenant.quartiles} />
                </div>
                <div className="lg:col-span-1">
                  <AssessmentAlert alerts={tenant.assessmentAlerts} />
                </div>
              </div>

              {/* Categories of workers, Art. 9(g) */}
              <CategoriesOfWorkersTable data={tenant.categoriesOfWorkers} />

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div>{t('dashboard.footer')}</div>
                <div className="flex items-center gap-3">
                  <span>{t('dashboard.lastSync')} {dateTime(new Date())}</span>
                </div>
              </div>

              {/* German translation disclaimer, visible only when DE active */}
              {i18n.language === 'de' && (
                <div className="mt-3 px-3 py-2 rounded-md bg-warning/5 border border-warning/20 text-[11px] text-warning italic flex items-start gap-2">
                  <span className="font-semibold not-italic">⚠</span>
                  <span>{t('disclaimer.germanTranslation')}</span>
                </div>
              )}
            </div>

            {isLoading && (
              <div className="fixed inset-0 bg-background/60 backdrop-blur-sm z-40 flex items-center justify-center pointer-events-none">
                <div className="bg-card border border-border rounded-lg p-4 shadow-elevated">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-solid"></div>
                    <span className="text-sm text-foreground font-medium">{t('dashboard.refreshing')}</span>
                  </div>
                </div>
              </div>
            )}

            <ReportGenerationToast
              state={reportState}
              onDismiss={() => setReportState({ phase: 'idle' })}
            />
          </div>
        </main>
      </div>
    </>
  );
};

export default AnalyticsDashboard;
